import { useIntl } from 'react-intl';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { formatPortalFreshness } from '../artistPortalCardState';
import { formatCatalogDay } from '../catalogDates';
import { removeHttpAndSlash } from '../../utils/removeHttpRegex';
import { SoundCloudCardState } from './soundCloudCardState';
import { soundCloudCardMessages as m } from './soundCloudCardMessages';

export interface SoundCloudCardTexts {
  handle: string;
  note: string;
  line: string | null;
  lineTone: 'plain' | 'ok' | 'bad';
  /** The line has a spinner (connecting, first read). */
  isWorking: boolean;
}

/** The handle, the note and the state line of each card state (design a2). */
export function useSoundCloudCardTexts({
  connection,
  state,
  now,
}: {
  connection: ServiceConnection;
  state: SoundCloudCardState;
  now?: number;
}): SoundCloudCardTexts {
  const intl = useIntl();
  const name = connection.artist?.name ?? '';
  const profile =
    connection.artistProfileUrl === null || connection.artistProfileUrl === ''
      ? intl.formatMessage(m.connectedAs, { name })
      : removeHttpAndSlash(connection.artistProfileUrl);
  const join = (first: string, second: string | null) => (second === null ? first : `${first} ${second}`);
  const lastDay = connection.syncedThrough ?? connection.lastSyncAt?.slice(0, 10) ?? null;

  switch (state) {
    case 'connecting':
      return {
        handle: intl.formatMessage(m.waiting),
        note: intl.formatMessage(m.noteConnecting),
        line: intl.formatMessage(m.opening),
        lineTone: 'plain',
        isWorking: true,
      };
    case 'firstRead':
      return {
        handle: intl.formatMessage(m.connectedAs, { name }),
        note: intl.formatMessage(m.noteFirstRead),
        line: intl.formatMessage(m.waitingFirstSync),
        lineTone: 'plain',
        isWorking: true,
      };
    case 'connected':
      return {
        handle: profile,
        note: join(intl.formatMessage(m.noteConnected), intl.formatMessage(m.noteCanUpload)),
        line: formatPortalFreshness(intl, connection, now ?? Date.now()) ?? null,
        lineTone: 'ok',
        isWorking: false,
      };
    case 'reconnect':
      return {
        handle: intl.formatMessage(m.signedOut),
        note: join(
          intl.formatMessage(m.noteReconnect),
          lastDay === null ? null : intl.formatMessage(m.noteReconnectStop, { day: formatCatalogDay(intl, lastDay) })
        ),
        line: lastDay === null ? null : intl.formatMessage(m.lastSync, { day: formatCatalogDay(intl, lastDay) }),
        lineTone: 'bad',
        isWorking: false,
      };
    case 'notConnected':
    case 'failed':
      return {
        handle: intl.formatMessage(m.notConnected),
        note: join(intl.formatMessage(m.noteNotConnected), intl.formatMessage(m.noteAutoUpload)),
        line: null,
        lineTone: 'plain',
        isWorking: false,
      };
  }
}
